import { computed } from 'vue';
import { translate } from '@/composables/useCaptionSettings';

export const IS_MAC = /Mac/.test(navigator.platform);

// Modifier names as the keyboard labels them: ⌥ / ⇧ on a Mac, Alt / Shift elsewhere.
export const ALT_KEY = IS_MAC ? '⌥' : 'Alt';
export const SHIFT_KEY = IS_MAC ? '⇧' : 'Shift';

// The Green Screen tip is only worth showing where there is a keyboard to press.
export const hasFinePointer = () => window.matchMedia && window.matchMedia('(pointer: fine)').matches;

// The room's shortcuts, as the cheat sheet lists them. Each entry is a sequence of keys
// (shown as key caps, optionally followed by text) or a gesture (shown as text). Keep in step with setKeyboardShortcuts
// and the caption drag handlers in ConferenceRoom.
export function useKeyboardShortcutGroups(mode) {
  return computed(() => {
    const general = {
      key: 'general',
      title: translate('shortcuts_group_general', 'General'),
      items: [
        { keys: ['Space'], label: translate('shortcuts_talk', 'Start or stop talking') },
        { keys: ['Esc'], label: translate('shortcuts_cancel_recording', 'Cancel recording') },
        { keys: ['+', '−'], label: translate('shortcuts_font_size', 'Increase / decrease font size') },
        { keys: [ALT_KEY, 'B'], label: translate('shortcuts_toggle_bars', 'Hide or show the toolbars') },
        { keys: ['?'], label: translate('shortcuts_show', 'Show keyboard shortcuts') },
      ],
    };
    const greenScreen = {
      key: 'chromakey',
      title: translate('shortcuts_group_green_screen', 'Green Screen'),
      items: [
        { keys: [ALT_KEY, 'H'], label: translate('shortcuts_toggle_captions', 'Hide or show captions') },
        { keys: ['←', '↑', '→', '↓'], label: translate('shortcuts_move_captions', 'Move captions') },
        { keys: [SHIFT_KEY], after: translate('shortcuts_plus_arrows', '+ arrows'), label: translate('shortcuts_move_captions_faster', 'Move captions faster') },
        { keys: ['G'], label: translate('shortcuts_stage_frame', 'Show or hide the 16:9 frame') },
        { gesture: translate('shortcuts_gesture_alt_drag', `Hold ${ALT_KEY} while dragging`), label: translate('shortcuts_move_freely', 'Move without snapping') },
        { gesture: translate('shortcuts_gesture_double_click', 'Double-click captions'), label: translate('shortcuts_reset_position', 'Reset caption position') },
      ],
    };
    // The current view's shortcuts come first.
    return mode.value === 'chromakey' ? [greenScreen, general] : [general, greenScreen];
  });
}
